import type { StripeTierName } from "../services/stripe";

/**
 * The plan a visitor chose before they had an account.
 *
 * The pricing page and the landing page's plan cards send a signed-out
 * visitor to registration. Until 2026-10-06 the choice was dropped there:
 * registration always went to first run, so nobody who picked PLUS on the
 * way in ever reached checkout. The choice now rides along as `?plan=` and is
 * kept here until registration or sign-in finishes, which then opens
 * checkout for it.
 */

const KEY = "healthpass.planIntent";
/** A choice older than this is not acted on: the visitor has moved on. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

const PLANS: Record<string, StripeTierName> = {
  plus: "PLUS",
  premium: "PREMIUM",
  plus_single: "PLUS_SINGLE",
  premium_single: "PREMIUM_SINGLE",
};

/** "PLUS SINGLE" → "?plan=plus_single"; "" for a plan nobody pays for. */
export function planQuery(planName: string): string {
  const key = planName.trim().toLowerCase().replace(/\s+/g, "_");
  return key in PLANS ? `?plan=${key}` : "";
}

/** The paid plan named in a query string, or null. */
export function planFromSearch(search: string): StripeTierName | null {
  const value = new URLSearchParams(search).get("plan");
  return value ? (PLANS[value.toLowerCase()] ?? null) : null;
}

export function rememberPlanIntent(search: string): void {
  const plan = planFromSearch(search);
  if (!plan) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ plan, at: Date.now() }));
  } catch {
    // Without storage the choice still works while the page stays open.
  }
}

/** The remembered plan, cleared as it is read; null when none or stale. */
export function takePlanIntent(now = Date.now()): StripeTierName | null {
  try {
    const raw = localStorage.getItem(KEY);
    localStorage.removeItem(KEY);
    if (!raw) return null;
    const { plan, at } = JSON.parse(raw) as { plan?: string; at?: number };
    if (!at || now - at > MAX_AGE_MS) return null;
    return Object.values(PLANS).includes(plan as StripeTierName)
      ? (plan as StripeTierName)
      : null;
  } catch {
    return null;
  }
}
